/**
 * Push notifications: Web Push (PWA) and native (FCM / APNs through `@capacitor/push-notifications`).
 *
 * Native token lifecycle:
 * - One `registration` / `registrationError` listener for the life of the app (never `removeAllListeners`, which
 *   used to wipe the notification-tap routing in nativePushActions.ts). Every token the OS hands us, including
 *   FCM rotations, is upserted to the backend.
 * - The token is persisted (`lh:push-token`), so Settings knows push is on after a restart.
 * - `syncNativePushRegistration()` re-registers silently on an authenticated launch (and on resume, at most hourly)
 *   when permission is already granted; it never prompts.
 * - `releaseNativePushForLogout()` unregisters the token from the backend before sign-out, so the device stops
 *   receiving the previous account's mail and agent pushes.
 */
import { computed, onMounted, ref } from 'vue'
import {
    getVapidPublicKey,
    registerDeviceToken,
    subscribePush,
    unregisterDeviceToken,
    unsubscribePush,
} from '@/api/push.api'
import { getNativePlatform, isNativePlatform } from '@/utils/platform'

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported'
type PushResult = { ok: boolean, error?: string }

/** Where the native device token is kept between launches. */
export const NATIVE_PUSH_TOKEN_KEY = 'lh:push-token'

function readStoredToken(): string | null {
    try {
        return localStorage.getItem(NATIVE_PUSH_TOKEN_KEY) || null
    }
    catch {
        return null
    }
}

/** Set when the creator turned push off in Settings, so a silent re-registration never turns it back on. */
const NATIVE_PUSH_OFF_KEY = 'lh:push-off'

function pushTurnedOff(): boolean {
    try {
        return localStorage.getItem(NATIVE_PUSH_OFF_KEY) === '1'
    }
    catch {
        return false
    }
}

function setPushTurnedOff(off: boolean) {
    try {
        if (off) localStorage.setItem(NATIVE_PUSH_OFF_KEY, '1')
        else localStorage.removeItem(NATIVE_PUSH_OFF_KEY)
    }
    catch {
        // Storage blocked.
    }
}

function storeToken(token: string | null) {
    try {
        if (token) localStorage.setItem(NATIVE_PUSH_TOKEN_KEY, token)
        else localStorage.removeItem(NATIVE_PUSH_TOKEN_KEY)
    }
    catch {
        // Storage blocked: the token lasts for this session.
    }
}

// Module-level singletons so every consumer shares one source of truth.
const permission = ref<PermissionState>('default')
const isSubscribed = ref(false)
const busy = ref(false)
let nativeDeviceToken: string | null = isNativePlatform() ? readStoredToken() : null

const NATIVE_REGISTER_TIMEOUT_MS = 20_000
/** Silent re-registration on resume runs at most this often (launch always runs). */
const NATIVE_RESYNC_MIN_MS = 60 * 60 * 1000

export function isStandalone(): boolean {
    if (isNativePlatform()) return true
    if (typeof window === 'undefined') return false

    // Regular browser tab — not an installed / home-screen web app.
    if (window.matchMedia?.('(display-mode: browser)')?.matches) {
        return false
    }

    // iOS Safari home-screen web app.
    const iosStandalone = (window.navigator as any).standalone === true
    if (iosStandalone) return true

    // Installed PWA (manifest display: standalone or minimal-ui on some Android launchers).
    return (
        window.matchMedia?.('(display-mode: standalone)')?.matches === true
        || window.matchMedia?.('(display-mode: minimal-ui)')?.matches === true
    )
}

function pushSupported(): boolean {
    if (isNativePlatform()) return true
    return (
        typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window
    )
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
}

async function getRegistration(timeoutMs = 8000): Promise<ServiceWorkerRegistration | null> {
    if (!pushSupported()) return null
    try {
        const ready = navigator.serviceWorker.ready
        if (timeoutMs <= 0) {
            return (await ready) || null
        }
        const registration = await Promise.race([
            ready,
            new Promise<null>((resolve) => {
                window.setTimeout(() => resolve(null), timeoutMs)
            }),
        ])
        return registration || null
    }
    catch {
        return null
    }
}

async function refreshStateNative(): Promise<void> {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    try {
        const { receive } = await PushNotifications.checkPermissions()
        permission.value = receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'default'
        isSubscribed.value = receive === 'granted' && Boolean(nativeDeviceToken)
    }
    catch {
        permission.value = 'unsupported'
        isSubscribed.value = false
    }
}

// ── native token: one listener pair for the app's lifetime ──────────────────────────────────────────────
let nativeListeners: Promise<void> | null = null
let tokenWaiters: Array<(result: PushResult) => void> = []

function settleTokenWaiters(result: PushResult) {
    const waiters = tokenWaiters
    tokenWaiters = []
    for (const resolve of waiters) resolve(result)
}

async function onNativeToken(token: string) {
    const platform = getNativePlatform()
    if (platform !== 'ios' && platform !== 'android') {
        settleTokenWaiters({ ok: false, error: 'Could not register for push notifications.' })
        return
    }
    const previous = nativeDeviceToken
    const ok = await registerDeviceToken(token, platform)
    if (ok) {
        nativeDeviceToken = token
        storeToken(token)
        isSubscribed.value = true
        // The OS rotated the token (reinstall, FCM refresh): the old one is dead weight on the backend.
        if (previous && previous !== token) void unregisterDeviceToken(previous)
    }
    settleTokenWaiters(ok
        ? { ok: true }
        : { ok: false, error: 'Could not register this device for push notifications.' })
}

function ensureNativeListeners(): Promise<void> {
    if (!nativeListeners) {
        nativeListeners = (async () => {
            const { PushNotifications } = await import('@capacitor/push-notifications')
            await PushNotifications.addListener('registration', (token) => {
                void onNativeToken(token.value)
            })
            await PushNotifications.addListener('registrationError', (error) => {
                settleTokenWaiters({ ok: false, error: error?.error || 'Could not register for push notifications.' })
            })
        })().catch((err) => {
            nativeListeners = null
            throw err
        })
    }
    return nativeListeners
}

/** Ask the OS for the device token and upsert it. Permission must already be granted. */
async function requestNativeToken(): Promise<PushResult> {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    await ensureNativeListeners()
    const result = new Promise<PushResult>((resolve) => {
        const timeoutId = window.setTimeout(() => {
            tokenWaiters = tokenWaiters.filter((w) => w !== done)
            resolve({
                ok: false,
                error: 'Push registration timed out. Confirm Push Notifications is enabled for this build and try again.',
            })
        }, NATIVE_REGISTER_TIMEOUT_MS)
        const done = (r: PushResult) => {
            window.clearTimeout(timeoutId)
            resolve(r)
        }
        tokenWaiters.push(done)
    })
    await PushNotifications.register()
    return result
}

async function enableNative(): Promise<PushResult> {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    try {
        let permStatus = await PushNotifications.checkPermissions()
        if (permStatus.receive === 'prompt' || permStatus.receive === 'prompt-with-rationale') {
            permStatus = await PushNotifications.requestPermissions()
        }
        permission.value = permStatus.receive === 'granted'
            ? 'granted'
            : permStatus.receive === 'denied'
                ? 'denied'
                : 'default'
        if (permStatus.receive !== 'granted') {
            return { ok: false, error: 'Notification permission was not granted.' }
        }
        setPushTurnedOff(false)
        return await requestNativeToken()
    }
    catch (e: any) {
        return { ok: false, error: e?.message || 'Could not enable push notifications.' }
    }
}

let lastNativeSyncAt = 0
let nativeSyncInflight: Promise<void> | null = null

/**
 * Re-register this device silently when the creator already allowed notifications: picks up token rotation,
 * reinstalls and a previous account's sign-out. Never prompts. `force` skips the hourly throttle (launch).
 */
export function syncNativePushRegistration(opts: { force?: boolean } = {}): Promise<void> {
    if (!isNativePlatform()) return Promise.resolve()
    if (nativeSyncInflight) return nativeSyncInflight
    const now = Date.now()
    if (!opts.force && now - lastNativeSyncAt < NATIVE_RESYNC_MIN_MS) return Promise.resolve()
    lastNativeSyncAt = now
    nativeSyncInflight = (async () => {
        try {
            const { PushNotifications } = await import('@capacitor/push-notifications')
            const { receive } = await PushNotifications.checkPermissions()
            permission.value = receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'default'
            if (receive !== 'granted' || busy.value || pushTurnedOff()) {
                isSubscribed.value = receive === 'granted' && Boolean(nativeDeviceToken)
                return
            }
            const result = await requestNativeToken()
            if (!result.ok) isSubscribed.value = Boolean(nativeDeviceToken)
        }
        catch {
            // Plugin missing or the bridge failed: leave the stored state alone.
        }
    })().finally(() => {
        nativeSyncInflight = null
    })
    return nativeSyncInflight
}

/**
 * Before sign-out: stop this device receiving the account's pushes (backend unregister needs the session, so
 * call it first). The OS registration stays; the next account to sign in re-registers silently.
 */
export async function releaseNativePushForLogout(): Promise<void> {
    if (!isNativePlatform()) return
    const token = nativeDeviceToken ?? readStoredToken()
    nativeDeviceToken = null
    storeToken(null)
    isSubscribed.value = false
    lastNativeSyncAt = 0
    if (token) {
        await unregisterDeviceToken(token).catch(() => false)
    }
    try {
        const { PushNotifications } = await import('@capacitor/push-notifications')
        await PushNotifications.removeAllDeliveredNotifications()
    }
    catch {
        // Nothing delivered, or the plugin is unavailable.
    }
}

async function disableNative(): Promise<boolean> {
    const { PushNotifications } = await import('@capacitor/push-notifications')
    try {
        if (nativeDeviceToken) {
            await unregisterDeviceToken(nativeDeviceToken)
        }
        try {
            await PushNotifications.unregister()
        }
        catch {
            // Older plugin builds may not expose unregister — ignore.
        }
        await PushNotifications.removeAllDeliveredNotifications()
        nativeDeviceToken = null
        storeToken(null)
        setPushTurnedOff(true)
        isSubscribed.value = false
        return true
    }
    catch {
        return false
    }
}

async function refreshState(): Promise<void> {
    if (isNativePlatform()) {
        return refreshStateNative()
    }
    if (!pushSupported()) {
        permission.value = 'unsupported'
        isSubscribed.value = false
        return
    }
    permission.value = Notification.permission as PermissionState
    const registration = await getRegistration()
    if (!registration) {
        isSubscribed.value = false
        return
    }
    try {
        const existing = await registration.pushManager.getSubscription()
        isSubscribed.value = Boolean(existing)
    }
    catch {
        isSubscribed.value = false
    }
}

async function enable(): Promise<PushResult> {
    if (isNativePlatform()) {
        if (busy.value) return { ok: false }
        busy.value = true
        try {
            return await enableNative()
        }
        finally {
            busy.value = false
        }
    }
    if (!pushSupported()) {
        return { ok: false, error: 'Push notifications are not supported on this device.' }
    }
    if (busy.value) return { ok: false }
    busy.value = true
    try {
        const result = await Notification.requestPermission()
        permission.value = result as PermissionState
        if (result !== 'granted') {
            return { ok: false, error: 'Notification permission was not granted.' }
        }

        const keyResponse = await getVapidPublicKey()
        if (!keyResponse?.configured || !keyResponse.public_key) {
            return { ok: false, error: 'Push notifications are not available right now.' }
        }

        const publicKey = (keyResponse.public_key || '').trim()
        if (!publicKey) {
            return { ok: false, error: 'Push notifications are not available right now.' }
        }

        const registration = await getRegistration()
        if (!registration) {
            return { ok: false, error: 'Service worker is not ready yet. Try again.' }
        }

        let subscription = await registration.pushManager.getSubscription()
        if (!subscription) {
            const applicationServerKey = urlBase64ToUint8Array(publicKey)
            if (applicationServerKey.length !== 65) {
                return {
                    ok: false,
                    error: 'Invalid push configuration. contact us at hello@joinlighthouse.ai if this persists.',
                }
            }
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey,
            })
        }

        const ok = await subscribePush(subscription.toJSON() as PushSubscriptionJSON, navigator.userAgent)
        isSubscribed.value = ok
        return ok ? { ok: true } : { ok: false, error: 'Could not register for push notifications.' }
    }
    catch (e: any) {
        return { ok: false, error: e?.message || 'Could not enable push notifications.' }
    }
    finally {
        busy.value = false
    }
}

async function disable(): Promise<boolean> {
    if (busy.value) return false
    if (isNativePlatform()) {
        busy.value = true
        try {
            return await disableNative()
        }
        finally {
            busy.value = false
        }
    }
    if (!pushSupported()) return false
    busy.value = true
    try {
        const registration = await getRegistration()
        if (!registration) return false
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
            const endpoint = subscription.endpoint
            await subscription.unsubscribe()
            await unsubscribePush(endpoint)
        }
        isSubscribed.value = false
        return true
    }
    catch {
        return false
    }
    finally {
        busy.value = false
    }
}

export function usePushNotifications() {
    onMounted(() => {
        void refreshState()
    })

    return {
        // state
        permission: computed(() => permission.value),
        isSubscribed: computed(() => isSubscribed.value),
        busy: computed(() => busy.value),
        // capability
        isStandalone,
        pushSupported,
        // actions
        enable,
        disable,
        refreshState,
    }
}
